// Centralised service catalog for AuthorOS.
// A sub-service can be:
//   - single-price (uses `price` + `billing`)
//   - multi-plan (has `plans` array; each plan can have `unitRate`+`unit`+`allowsQuantity`)
//   - a calculator (has `type: "calculator"`; rendered by a dedicated component)

export const SERVICES = [
  {
    id: "01",
    slug: "book-publishing",
    icon: "BookOpen",
    label: "Book Publishing",
    tagline: "From manuscript to shelf.",
    description:
      "End-to-end publishing for authors who want their book taken seriously \u2014 editing, design, ISBN, print & digital delivery.",
    priceFrom: "\u20B91,00,000",
    responseTime: "24-hour response",
    builderKicker: "Your Publishing Plan",
    builderTitle: "Build your book's release plan",
    builderCopy:
      "Add publishing sub-services below to compose your book's release scope \u2014 every add-on stacks into a live plan.",
    subs: [
      {
        code: "01.1",
        slug: "visual-full-color",
        title: "Visual (Full Color)",
        subtitle: "Illustrated, image-heavy books",
        description:
          "Full-color interior for art, cookbook, coffee-table, children\u2019s or photography titles. Includes color-managed layout, print-ready CMYK exports, and premium paper recommendations.",
        price: "\u20B94,50,000",
        priceNote: "one-time \u00b7 GST extra",
        billing: "ONE-TIME + GST",
        timeline: "8\u201310 weeks",
        deliverables: 5,
        included: [
          "Full-color interior layout",
          "Color-managed CMYK exports",
          "Cover design (front + back + spine)",
          "ISBN registration + metadata",
          "Print-ready + digital masters",
        ],
      },
      {
        code: "01.2",
        slug: "black-and-white",
        title: "Black & White",
        subtitle: "Standard interior for novels & non-fiction",
        description:
          "Classic B&W interior for novels, memoirs and non-fiction. Includes copy-edit pass, professional typesetting and a cover system built for shelves and thumbnails.",
        price: "\u20B995,000",
        priceNote: "one-time \u00b7 GST extra",
        billing: "ONE-TIME + GST",
        timeline: "5\u20137 weeks",
        deliverables: 4,
        included: [
          "Copy-edit pass on manuscript",
          "Typeset B&W interior",
          "Cover design (front + back + spine)",
          "ISBN + metadata registration",
          "Print-ready PDF + eBook masters",
        ],
      },
      {
        code: "01.3",
        slug: "audio-edition",
        title: "Audio Edition",
        subtitle: "Studio-narrated audiobook edition",
        description:
          "Voice casting, studio narration, mastering and platform delivery. Priced by finished runtime.",
        price: "From \u20B975,000",
        priceNote: "priced by runtime",
        billing: "PRICED BY RUNTIME + GST",
        timeline: "4\u20136 weeks",
        deliverables: 5,
        plansHeading: "Choose a runtime plan",
        planColumns: 2,
        plans: [
          {
            key: "up-to-5",
            name: "Up to 5 hours",
            price: "\u20B975,000",
            priceNote: "one-time \u00b7 GST extra",
            popular: true,
            features: [
              "Finished runtime \u2264 5 hours",
              "Voice casting (3 shortlisted narrators)",
              "Studio recording + directed sessions",
              "Mastering to ACX / Spotify spec",
              "Chapter-wise WAV + MP3 delivery",
              "Platform upload assistance",
            ],
          },
          {
            key: "more-than-5",
            name: "More than 5 hours",
            price: "\u20B91,25,000",
            priceNote: "one-time \u00b7 GST extra",
            features: [
              "Finished runtime > 5 hours",
              "Voice casting (3 shortlisted narrators)",
              "Extended studio recording",
              "Mastering to ACX / Spotify spec",
              "Chapter-wise WAV + MP3 delivery",
              "Platform upload assistance",
            ],
          },
        ],
        included: [
          "Voice casting (3 shortlisted narrators)",
          "Studio recording",
          "Mastering to ACX / Spotify spec",
          "Chapter-wise WAV + MP3 delivery",
          "Platform upload assistance",
        ],
      },
    ],
  },
  {
    id: "02",
    slug: "marketing-of-book",
    icon: "Megaphone",
    label: "Marketing of Book",
    tagline: "Make it impossible to ignore.",
    description:
      "Campaigns that move copies. Positioning, launch strategy, influencer & performance marketing designed for books.",
    priceFrom: "\u20B925,000",
    responseTime: "24-hour response",
    builderKicker: "Your Marketing Plan",
    builderTitle: "Build your book's launch plan",
    builderCopy:
      "Add marketing sub-services below to compose your book's marketing mix \u2014 every add-on stacks into a live plan.",
    subs: [
      { code: "02.1", slug: "d2c-ads", title: "D2C Ads", subtitle: "Meta + Google for your D2C store", price: "\u20B91,00,000", priceNote: "per batch \u00b7 GST extra", billing: "PER BATCH + GST", timeline: "Live in 10 days", deliverables: 6, cardType: "logos", logos: [{ src: "https://cdn.simpleicons.org/meta/0866FF", alt: "Meta" }, { src: "https://cdn.simpleicons.org/googleads/4285F4", alt: "Google Ads" }], description: "Direct-to-reader ad system on Meta and Google. We build creatives, landing pages and structured campaigns. Buy multiple batches to scale.", unitRate: 100000, unitLabel: "batch", defaultQuantity: 1, minQuantity: 1, maxQuantity: 24, allowsQuantity: true, included: ["Meta + Google campaign setup", "5 static + 3 video creatives per batch", "Landing page + pixel setup", "Weekly performance report"] },
      { code: "02.2", slug: "landing-page-for-the-book", title: "Landing Page for the Book", subtitle: "High-converting book landing page", price: "\u20B930,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "2\u20133 weeks", deliverables: 4, description: "A high-converting single-page site that positions your book, showcases reviews, and closes the sale. Fast, mobile-first, integrated with your ad stack.", included: ["Custom design + copy", "Mobile-first responsive build", "Pixel + analytics wiring", "Payment / pre-order flow"] },
      { code: "02.3", slug: "creative-for-the-book", title: "Creative for the Book \u2014 Video", subtitle: "Ad videos, reels, cover animations", price: "\u20B915,000", priceNote: "per video \u00b7 GST extra", billing: "PER VIDEO + GST", timeline: "2 weeks", deliverables: 12, description: "Studio-produced video creatives \u2014 reels, cover animations, ad units. Buy per video, scale up as your campaigns need.", unitRate: 15000, unitLabel: "video", defaultQuantity: 3, minQuantity: 1, maxQuantity: 100, allowsQuantity: true, included: ["Concept + script per video", "Studio production", "Platform-specific exports", "1 round of revisions per video"] },
      { code: "02.3b", slug: "creative-for-the-book-images", title: "Creative for the Book \u2014 Images", subtitle: "Ad images, banners, static creatives", price: "\u20B95,000", priceNote: "per image \u00b7 GST extra", billing: "PER IMAGE + GST", timeline: "1\u20132 weeks", deliverables: 5, description: "Studio-designed static creatives \u2014 ad units, banners, quote cards and social posts. Buy per image, scale as needed.", unitRate: 5000, unitLabel: "image", defaultQuantity: 5, minQuantity: 1, maxQuantity: 200, allowsQuantity: true, included: ["Concept + design per image", "Platform-specific exports", "Copy + caption support", "1 round of revisions per image"] },
      {
        code: "02.4",
        slug: "pr",
        title: "PR",
        subtitle: "Press, podcast & op-ed placements",
        description:
          "Editorial-led PR pushes across Indian & international press, podcasts and long-form outlets. Pay for placements, not promises.",
        price: "From \u20B940,000",
        priceNote: "3 plans available",
        billing: "3 PLANS",
        timeline: "3\u20134 weeks",
        deliverables: 3,
        plansHeading: "Choose a PR tier",
        planColumns: 3,
        plans: [
          {
            key: "starter",
            name: "Starter",
            price: "\u20B940,000",
            priceNote: "3 placements \u00b7 GST extra",
            features: [
              "3 vetted placements",
              "Angle & press-kit build",
              "Targeted outlet outreach",
              "Clip pack + report",
            ],
          },
          {
            key: "growth",
            name: "Growth",
            price: "\u20B980,000",
            priceNote: "8 placements \u00b7 GST extra",
            popular: true,
            features: [
              "8 vetted placements",
              "National + regional outlets",
              "1 podcast + 1 op-ed slot",
              "Clip pack + reporting",
            ],
          },
          {
            key: "national",
            name: "National",
            price: "\u20B92,00,000",
            priceNote: "15+ placements \u00b7 GST extra",
            features: [
              "15+ premium placements",
              "National print + digital press",
              "2 podcast + 2 op-ed slots",
              "Post-launch amplification",
            ],
          },
        ],
        included: [
          "Angle & press kit build",
          "Targeted outlet outreach",
          "Placement in vetted publications",
          "Reporting & clip pack",
        ],
      },
      {
        code: "02.5",
        slug: "giveaway-of-your-book",
        title: "Giveaway of your Book",
        subtitle: "Distribute copies to seed reviews & buzz",
        description: "A managed book giveaway across social, Goodreads and email lists \u2014 designed to seed reviews and early buzz. You supply the stock; we handle everything else.",
        price: "\u20B9120",
        priceNote: "per unit (stock cost extra)",
        billing: "PER UNIT + GST",
        timeline: "2 weeks",
        deliverables: 3,
        unitRate: 120,
        unitLabel: "unit",
        defaultQuantity: 100,
        minQuantity: 25,
        maxQuantity: 2000,
        allowsQuantity: true,
        included: [
          "Multi-channel giveaway build",
          "Winner selection & fulfilment",
          "Review-nudge sequence",
        ],
      },
      { code: "02.6", slug: "amazon-ads", title: "Amazon Ads", subtitle: "Sponsored Products & brand ads", price: "\u20B950,000", priceNote: "per campaign \u00b7 GST extra", billing: "PER CAMPAIGN + GST", timeline: "Live in 7 days", deliverables: 4, cardType: "logos", logos: [{ src: "https://cdn.simpleicons.org/amazon/FF9900", alt: "Amazon Ads" }], description: "Sponsored Products, Brands and Display ads on Amazon India \u2014 keyword-tuned and structured for profit at scale. Buy campaigns as you scale.", unitRate: 50000, unitLabel: "campaign", defaultQuantity: 1, minQuantity: 1, maxQuantity: 24, allowsQuantity: true, included: ["Sponsored Products setup", "Brand + Display campaigns", "Weekly bid tuning", "Reporting + review calls"] },
      { code: "02.7", slug: "quick-commerce-ads", title: "Quick Commerce Ads", subtitle: "Blinkit \u00b7 Zepto \u00b7 Instamart placements", price: "\u20B960,000", priceNote: "per campaign \u00b7 GST extra", billing: "PER CAMPAIGN + GST", timeline: "Live in 2 weeks", deliverables: 4, description: "Q-comm ads across Blinkit, Zepto and Swiggy Instamart \u2014 category placement, search and homepage banners. Buy per campaign, scale as needed.", unitRate: 60000, unitLabel: "campaign", defaultQuantity: 1, minQuantity: 1, maxQuantity: 12, allowsQuantity: true, included: ["Category + search placement", "Homepage banner slots", "Creative + copy build", "Weekly optimisation"] },
      {
        code: "02.8",
        slug: "book-store-paid-placement",
        title: "Book Store Marketing",
        subtitle: "City & airport store placements",
        description:
          "On-ground marketing in Indian bookstores. Two 3-month plans \u2014 pick city bookstores, airport terminal stores or both.",
        price: "From \u20B945,000",
        priceNote: "per store \u00b7 3 months \u00b7 GST extra",
        billing: "3-MONTH PLAN",
        timeline: "4\u20136 weeks",
        deliverables: 4,
        externalLink: { label: "See list of bookstores", href: "https://stores.zebralearn.com" },
        plansHeading: "Choose a placement tier",
        planColumns: 2,
        plans: [
          {
            key: "city",
            name: "City Bookstores",
            price: "\u20B945,000",
            priceNote: "\u20B915,000 / store / month \u00b7 3 months \u00b7 GST extra",
            popular: true,
            features: [
              "3-month placement in city bookstore",
              "Chain + indie network",
              "Endcap / window slot",
              "Weekly sell-through report",
            ],
          },
          {
            key: "airport",
            name: "Airport Stores",
            price: "\u20B975,000",
            priceNote: "\u20B925,000 / store / month \u00b7 3 months \u00b7 GST extra",
            features: [
              "3-month placement in airport terminal store",
              "Premium travel-reader footfall",
              "Endcap / spinner setup",
              "Weekly sell-through report",
            ],
          },
        ],
        included: [
          "Store onboarding & POS pipeline",
          "Endcap / spinner setup",
          "Sell-through reporting",
          "Access to 'See list of bookstores'",
        ],
      },
      { code: "02.9", slug: "international-ads", title: "International Ads", subtitle: "US \u00b7 UK \u00b7 UAE paid campaigns", price: "\u20B91,00,000", priceNote: "per campaign \u00b7 GST extra", billing: "PER CAMPAIGN + GST", timeline: "Live in 2 weeks", deliverables: 5, description: "Country-tuned Meta + Google + Amazon ads across the United States, United Kingdom and UAE. Buy campaigns as you scale.", unitRate: 100000, unitLabel: "campaign", defaultQuantity: 1, minQuantity: 1, maxQuantity: 12, allowsQuantity: true, included: ["Country-tuned campaign setup", "Localised creatives", "Amazon.com + Amazon.co.uk ads", "Weekly reporting"] },
      {
        code: "02.10",
        slug: "book-launch-event",
        title: "Book Launch Event",
        subtitle: "Simple or Prime launch event",
        price: "From \u20B92,00,000",
        priceNote: "2 plans available",
        billing: "2 PLANS",
        timeline: "6\u20138 weeks",
        deliverables: 6,
        description: "An end-to-end book launch event \u2014 venue, guests, press, streaming and post-event content \u2014 delivered by our production team.",
        plansHeading: "Choose a launch tier",
        planColumns: 2,
        plans: [
          {
            key: "simple",
            name: "Simple Launch",
            price: "\u20B92,00,000",
            priceNote: "up to 100 guests \u00b7 GST extra",
            features: [
              "Venue + AV production",
              "Up to 100 curated guests",
              "Livestream + recap reel",
              "Press outreach",
            ],
          },
          {
            key: "prime",
            name: "Prime Launch",
            price: "\u20B96,00,000",
            priceNote: "up to 300 guests \u00b7 GST extra",
            popular: true,
            features: [
              "Premium venue + F&B",
              "Up to 300 curated guests",
              "Multi-camera livestream",
              "Full press + influencer package",
              "Post-event content kit",
            ],
          },
        ],
        included: [
          "Venue + AV production",
          "Guest curation + RSVPs",
          "Livestream + post-event content",
          "Press outreach",
        ],
      },
      { code: "02.11", slug: "influencer-marketing-book-reviewers", title: "Influencer Marketing \u2014 Book Reviewers", subtitle: "Curated BookTok & Bookstagram reviewers", price: "\u20B950,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "3\u20134 weeks", deliverables: 5, description: "A curated push through BookTok and Bookstagram reviewers \u2014 seeding, briefing and reporting managed end-to-end.", included: ["10 curated reviewer seeds", "Brief + creative direction", "Post-scheduling & tracking", "Reporting + reshare pack"] },
      {
        code: "02.12",
        slug: "influencer-marketing",
        title: "Influencer Marketing",
        subtitle: "Broader creator partnerships (per unit)",
        price: "\u20B91,00,000",
        priceNote: "per unit \u00b7 GST extra",
        billing: "PER UNIT + GST",
        timeline: "4 weeks",
        deliverables: 4,
        description: "Broader creator partnerships across lifestyle, business and niche communities. Buy multiple units to scale.",
        unitRate: 100000,
        unitLabel: "unit",
        defaultQuantity: 1,
        minQuantity: 1,
        maxQuantity: 20,
        allowsQuantity: true,
        included: [
          "Creator shortlist per unit",
          "Rate negotiation",
          "Content sign-off & tracking",
          "Reporting per unit",
        ],
      },
      { code: "02.13", slug: "bookmark-marketing", title: "Bookmark Marketing", subtitle: "Branded bookmarks across every channel", price: "\u20B925,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "2\u20133 weeks", deliverables: 3, description: "Design and distribute branded bookmarks across bookstores, launches and inserts \u2014 a low-cost brand memory device.", included: ["Bookmark design", "Print + packaging", "Store + inserts distribution"] },
      { code: "02.14", slug: "email-marketing", title: "Email Marketing", subtitle: "Launch + evergreen email engine", price: "\u20B950,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "3 weeks", deliverables: 5, description: "Launch sequence + evergreen email engine \u2014 lists, flows, creative and platform setup.", included: ["Launch sequence design", "Evergreen post-launch flow", "List + segmentation setup", "Creative + copy build"] },
      {
        code: "02.15",
        slug: "whatsapp-marketing",
        title: "WhatsApp Marketing",
        subtitle: "Broadcasts, drips and DMs",
        price: "\u20B91.50",
        priceNote: "per message \u00b7 GST extra",
        billing: "PER MESSAGE + GST",
        timeline: "3 weeks",
        deliverables: 4,
        description: "A WhatsApp-first channel for your reader \u2014 broadcast, drip sequences and support built on the Business API. Pay only for messages sent.",
        unitRate: 1.5,
        unitLabel: "message",
        defaultQuantity: 5000,
        minQuantity: 500,
        maxQuantity: 500000,
        step: 500,
        allowsQuantity: true,
        included: [
          "Business API setup",
          "Broadcast + drip flows",
          "Creative + copy build",
          "Weekly performance report",
        ],
      },
      { code: "02.16", slug: "hyperlocal-marketing", title: "Hyperlocal Marketing", subtitle: "Newspaper cutouts + Q-comm marketing", price: "\u20B950,000", priceNote: "per city \u00b7 GST extra", billing: "PER CITY + GST", timeline: "3 weeks", deliverables: 4, description: "Hyperlocal push per city \u2014 newspaper inserts, Q-comm micro-ads and neighbourhood outdoor. Add multiple cities to scale.", unitRate: 50000, unitLabel: "city", defaultQuantity: 1, minQuantity: 1, maxQuantity: 30, allowsQuantity: true, included: ["Newspaper insert design + buy", "Q-comm micro-ad placements", "Neighbourhood outdoor buys", "Per-city reporting"] },
    ],
  },
  {
    id: "03",
    slug: "printing-of-book",
    icon: "Printer",
    label: "Printing of Book",
    tagline: "Paper that feels premium.",
    description:
      "Precision offset and digital print runs \u2014 paper, binding and finish choices that make bookstores stop and stare. Get an instant quote below.",
    priceFrom: "\u20B915,000",
    responseTime: "24-hour response",
    builderKicker: "Your Print Plan",
    builderTitle: "Configure your print run",
    builderCopy:
      "Enter your book\u2019s specs \u2014 the calculator prices it in real time. Add the run to your plan and we\u2019ll confirm the press within 24 hours.",
    subs: [
      {
        code: "03.1",
        slug: "print-run-calculator",
        title: "Print Run Calculator",
        subtitle: "Real-time per-copy print pricing",
        description:
          "Configure page size, binding, paper thickness, page count and print method. We price it live and lock the press within 24 hours.",
        type: "calculator",
        price: "Instant quote",
        priceNote: "priced by spec + quantity",
        billing: "INSTANT QUOTE",
        timeline: "2\u20134 weeks",
        deliverables: 4,
        included: [
          "Paper + binding advisory",
          "Press sourcing + QC",
          "Print-ready pre-flight",
          "Boxed + labelled delivery",
        ],
      },
    ],
  },
  {
    id: "04",
    slug: "distribution-of-book",
    icon: "Globe2",
    label: "Distribution of Book",
    tagline: "Everywhere your reader is.",
    description:
      "Get on every shelf and every screen. We handle the accounts, catalogs, logistics and reconciliation.",
    priceFrom: "\u20B915,000",
    responseTime: "24-hour response",
    builderKicker: "Your Distribution Plan",
    builderTitle: "Build your global distribution",
    builderCopy:
      "Add distribution sub-services to compose a country + channel mix that fits your book.",
    subs: [
      { code: "04.1", slug: "quick-commerce", title: "Quick Commerce", subtitle: "Blinkit \u00b7 Zepto \u00b7 Instamart", price: "\u20B940,000", priceNote: "per platform onboarding + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "3 weeks", deliverables: 3, description: "Onboarding, catalog and stock setup across Blinkit, Zepto and Swiggy Instamart so readers get your book in minutes. Setup fee per platform + 15% commission on sales.", included: ["Account onboarding per platform", "Catalog + imagery build", "Inventory + reconciliation", "15% sales commission (auto-billed)"] },
      { code: "04.2", slug: "amazon-india", title: "Amazon India", subtitle: "India\u2019s #1 online store", price: "Free", priceNote: "15% sales commission \u00b7 GST extra", billing: "FREE + 15% COMM.", timeline: "2\u20133 weeks", deliverables: 4, description: "Full Amazon India listing: A+ content, keyword-tuned copy, FBA/self-ship setup and review programme. Zero setup fee \u2014 we take 15% of sales.", included: ["A+ content build", "FBA / self-ship setup", "Keyword + PPC starter", "15% sales commission (auto-billed)"] },
      { code: "04.3", slug: "flipkart", title: "Flipkart", subtitle: "Flipkart marketplace listing", price: "Free", priceNote: "15% sales commission \u00b7 GST extra", billing: "FREE + 15% COMM.", timeline: "2\u20133 weeks", deliverables: 3, description: "Flipkart marketplace onboarding with high-CTR imagery, catalog and inventory sync. Zero setup fee \u2014 we take 15% of sales.", included: ["Seller onboarding", "Rich listing + imagery", "Inventory sync", "15% sales commission (auto-billed)"] },
      { code: "04.4", slug: "kindle-ebook", title: "Kindle (eBook)", subtitle: "KDP + reader-first metadata", price: "\u20B910,000", priceNote: "setup + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "1\u20132 weeks", deliverables: 3, description: "Reflowable eBook build, KDP setup and category/keyword strategy to rank inside Amazon Kindle. Setup fee + 15% commission on sales.", included: ["EPUB + MOBI masters", "KDP upload + metadata", "Category + keyword strategy", "15% sales commission (auto-billed)"] },
      { code: "04.5", slug: "d2c-storefront", title: "D2C Storefront", subtitle: "Your own book store", price: "\u20B980,000", priceNote: "setup + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "3\u20134 weeks", deliverables: 5, description: "A branded Shopify storefront so you own your reader relationship and margin. Setup fee + 15% commission on sales.", included: ["Shopify build + theme", "Payments + shipping", "Analytics + pixel wiring", "15% sales commission (auto-billed)"] },
      {
        code: "04.6",
        slug: "bookstores",
        title: "Bookstores",
        subtitle: "City & airport store placements",
        description:
          "On-ground distribution in Indian bookstores. Two 3-month plans \u2014 pick city bookstores, airport terminal stores or both.",
        price: "From \u20B945,000",
        priceNote: "per store \u00b7 3 months + 15% sales commission \u00b7 GST extra",
        billing: "3-MONTH PLAN + 15% COMM.",
        timeline: "4\u20136 weeks",
        deliverables: 4,
        externalLink: {
          label: "See list of bookstores",
          href: "https://stores.zebralearn.com",
        },
        plansHeading: "Choose a placement tier",
        planColumns: 2,
        plans: [
          {
            key: "city",
            name: "City Bookstores",
            price: "\u20B945,000",
            priceNote: "\u20B915,000 / store / month \u00b7 3 months + 15% commission \u00b7 GST extra",
            popular: true,
            features: [
              "3-month placement in city bookstore",
              "Chain + indie network",
              "Weekly sell-through report",
              "15% sales commission (auto-billed)",
            ],
          },
          {
            key: "airport",
            name: "Airport Stores",
            price: "\u20B975,000",
            priceNote: "\u20B925,000 / store / month \u00b7 3 months + 15% commission \u00b7 GST extra",
            features: [
              "3-month placement in airport terminal store",
              "Premium travel-reader footfall",
              "Endcap / spinner setup",
              "15% sales commission (auto-billed)",
            ],
          },
        ],
        included: [
          "Store onboarding & POS pipeline",
          "Monthly reorder ops",
          "Sell-through reporting",
          "15% sales commission (auto-billed)",
          "Access to \u2018See list of bookstores\u2019",
        ],
      },
      { code: "04.7", slug: "united-states", title: "United States", subtitle: "Amazon.com", price: "\u20B955,000", priceNote: "setup + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "4 weeks", deliverables: 3, description: "Print-on-demand + Kindle distribution across the United States via Amazon.com. Setup fee + 15% commission on sales.", included: ["Amazon.com listing", "Amazon KDP setup", "US metadata + tax setup", "15% sales commission (auto-billed)"] },
      { code: "04.8", slug: "united-kingdom", title: "United Kingdom", subtitle: "Amazon.co.uk + trade", price: "\u20B950,000", priceNote: "setup + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "4 weeks", deliverables: 3, description: "UK trade & online distribution, VAT/registration handling and cover-metadata localisation. Setup fee + 15% commission on sales.", included: ["Amazon.co.uk listing", "Trade distribution", "VAT + metadata setup", "15% sales commission (auto-billed)"] },
      { code: "04.9", slug: "united-arab-emirates", title: "United Arab Emirates", subtitle: "Gulf retail + online", price: "\u20B945,000", priceNote: "setup + 15% sales commission \u00b7 GST extra", billing: "SETUP + 15% COMM.", timeline: "4 weeks", deliverables: 3, description: "UAE-first distribution across major online and retail partners in the Gulf region. Setup fee + 15% commission on sales.", included: ["Online marketplace listing", "Retail partner onboarding", "Local logistics setup", "15% sales commission (auto-billed)"] },
    ],
  },
  {
    id: "05",
    slug: "audiobooks",
    icon: "Headphones",
    label: "Audiobooks",
    tagline: "Voice that carries.",
    description:
      "AI or studio-narrated audiobooks, mastered and distributed on Audible, Spotify and Zebralearn.",
    priceFrom: "\u20B930,000",
    responseTime: "24-hour response",
    builderKicker: "Your Audio Plan",
    builderTitle: "Build your audiobook production",
    builderCopy:
      "Add audio sub-services to compose narration, distribution and revenue share in one plan.",
    subs: [
      { code: "05.1", slug: "ai-narration", title: "AI Narration", subtitle: "Studio-grade AI narration", price: "\u20B930,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "1\u20132 weeks", deliverables: 4, description: "Neural-voice narration of your finished book with a human QA pass and mastered, platform-ready delivery.", included: ["Neural narration + QA", "Mastering to platform specs", "Chaptered file delivery", "Distribution-ready metadata"] },
      { code: "05.2", slug: "narration-up-to-5-hours", title: "Narration up to 5 hours", subtitle: "Human studio narration \u2264 5 hours", price: "\u20B975,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "3\u20134 weeks", deliverables: 5, description: "Booth-recorded human narration with a directed session, for books that finish under 5 hours of runtime.", included: ["Voice casting shortlist", "Directed booth recording", "Editing + mastering", "Chaptered WAV + MP3 masters", "Platform upload assistance"] },
      { code: "05.3", slug: "narration-more-than-5-hours", title: "Narration more than 5 hours", subtitle: "Human studio narration > 5 hours", price: "\u20B91,25,000", priceNote: "one-time \u00b7 GST extra", billing: "ONE-TIME + GST", timeline: "4\u20136 weeks", deliverables: 5, description: "Extended booth-recorded narration for longer books, with the same directed-session quality and platform-ready masters.", included: ["Voice casting shortlist", "Extended booth recording", "Editing + mastering", "Chaptered WAV + MP3 masters", "Platform upload assistance"] },
      { code: "05.4", slug: "audiobook-distribution", title: "Audiobook Distribution", subtitle: "Audible \u00b7 Spotify \u00b7 Zebralearn", price: "\u20B925,000", priceNote: "onboarding \u00b7 GST extra", billing: "ONBOARDING + GST", timeline: "2 weeks", deliverables: 3, description: "We onboard and distribute your audiobook to Audible, Spotify and Zebralearn.", included: ["Audible + Spotify upload", "Zebralearn placement", "Metadata + platform QA", "Monthly royalty reporting"] },
    ],
  },
];

export const QUICK_ACTIONS = [
  {
    id: "publish",
    title: "Publish my book",
    hint: "Start with a black & white publishing scope",
    to: "/service/book-publishing/black-and-white",
  },
  {
    id: "print",
    title: "Get a print quote",
    hint: "Real-time print calculator",
    to: "/service/printing-of-book/print-run-calculator",
  },
  {
    id: "amazon",
    title: "Launch on Amazon",
    hint: "Get your book live on Amazon India",
    to: "/service/distribution-of-book/amazon-india",
  },
  {
    id: "audio",
    title: "Turn it into an audiobook",
    hint: "AI or studio narration",
    to: "/service/audiobooks/ai-narration",
  },
];

export const TICKER_ITEMS = [
  "AuthorOS by Zebralearn",
  "Publish \u00b7 Print \u00b7 Distribute \u00b7 Market",
  "Audiobooks \u00b7 Bookstores \u00b7 Growth",
  "Est. Mumbai \u00b7 Global Delivery",
];

export const CONSULT_GOALS = [
  "Publishing",
  "Marketing",
  "Printing",
  "Distribution",
  "Bookstores",
  "Audiobooks",
];

export function totalSubServices() {
  return SERVICES.reduce((n, s) => n + s.subs.length, 0);
}

export function findService(slug) {
  return SERVICES.find((s) => s.slug === slug);
}

export function findSub(serviceSlug, subSlug) {
  const svc = findService(serviceSlug);
  if (!svc) return { service: null, sub: null };
  const sub = svc.subs.find((x) => x.slug === subSlug) || null;
  return { service: svc, sub };
}
